
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { format, subMonths, startOfMonth } from 'date-fns';

interface RevenueChartProps {
  detailed?: boolean;
}

export function RevenueChart({ detailed = false }: RevenueChartProps) {
  const { data: revenueData, isLoading } = useQuery({
    queryKey: ['revenue-data', detailed],
    queryFn: async () => {
      // Get subscribers by month for the last 6 months
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = startOfMonth(subMonths(new Date(), i));
        months.push(date);
      }

      const monthlyData = await Promise.all(
        months.map(async (month) => {
          const nextMonth = startOfMonth(subMonths(month, -1));
          
          // Count new subscribers in this month
          const { count: newSubscribers } = await supabase
            .from('subscribers')
            .select('*', { count: 'exact', head: true })
            .gte('created_at', month.toISOString())
            .lt('created_at', nextMonth.toISOString());

          // Count active paid subscribers at end of month
          const { data: activeSubscribers } = await supabase
            .from('subscribers')
            .select('subscription_tier, subscribed, created_at')
            .eq('subscribed', true)
            .lte('created_at', nextMonth.toISOString());

          // Calculate MRR (assuming $9.95 for premium)
          const mrr = activeSubscribers?.reduce((total, sub) => {
            if (sub.subscription_tier === 'premium') return total + 9.95;
            return total;
          }, 0) || 0;

          return {
            month: format(month, 'MMM yyyy'),
            newSubscribers: newSubscribers || 0,
            totalActive: activeSubscribers?.length || 0,
            mrr: parseFloat(mrr.toFixed(2))
          };
        })
      );

      return monthlyData;
    }
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{detailed ? 'Detailed Revenue Analytics' : 'Revenue Trend'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading revenue data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (detailed) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Monthly Recurring Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => [`$${value}`, 'MRR']} />
                <Line type="monotone" dataKey="mrr" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>New Subscribers by Month</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="newSubscribers" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => [`$${value}`, 'MRR']} />
            <Line type="monotone" dataKey="mrr" stroke="#8884d8" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
